import * as React from 'react';

import {
  useCreateSummary,
  useFetchSummarisationStatus,
} from '@/features/Summarisation/hooks';

export const useCreateSummaryHandler = ({
  deeplink,
  source,
}: UseCreateSummaryHandler) => {
  const createSummaryMutation = useCreateSummary();
  const { data, isLoading: isLoadingStatus } = useFetchSummarisationStatus({
    deeplink,
  });

  const isGeneratingSummary =
    (data?.status && ['PROCESSING', 'PENDING'].includes(data.status)) ||
    createSummaryMutation.isLoading;

  const disabled = isLoadingStatus || isGeneratingSummary;

  const isSummaryGenerated = !isLoadingStatus && data?.status === 'SUCCESS';

  const createSummary = React.useCallback(() => {
    createSummaryMutation.mutate({
      deeplink,
      source,
    });
  }, [createSummaryMutation, deeplink, source]);

  const handleViewSummary = React.useCallback(() => {
    if (isSummaryGenerated && data?.summary_pdf) {
      Object.assign(document.createElement('a'), {
        rel: 'noreferrer noopener',
        target: '_blank',
        href: data.summary_pdf,
      }).click();
    }
  }, [data, isSummaryGenerated]);

  return React.useMemo(
    () => ({
      createSummary,
      disabled,
      isGeneratingSummary,
      isSummaryGenerated,
      isSummaryStatusLoading: isLoadingStatus,
      handleViewSummary,
    }),
    [
      createSummary,
      disabled,
      handleViewSummary,
      isGeneratingSummary,
      isLoadingStatus,
      isSummaryGenerated,
    ]
  );
};

type UseCreateSummaryHandler = {
  deeplink: string;
  source: string;
};
